import {
  Avatar,
  makeStyles,
  Step,
  StepLabel,
  Stepper,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { colorSet, clsx } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';

interface ProgressStepProps {
  steps: { icon: React.ReactNode; label: string }[];
  activeStep?: number;
  isStopped?: boolean;
}

interface ProgressStepStyleProps {
  isStopped: boolean;
}

const ProgressStep: React.FunctionComponent<ProgressStepProps> = ({
  steps,
  activeStep,
  isStopped = false
}) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyles({ isStopped });

  return (
    <div className={classes.container}>
      <Stepper className={classes.root} alternativeLabel>
        {steps.map((step, index) => {
          const isActive = index === activeStep;
          return (
            <Step
              key={index}
              className={clsx(classes.step, {
                [classes.activeStep]: isActive
              })}
            >
              <StepLabel
                icon={
                  <Avatar
                    className={clsx(classes.stepIcon, {
                      [classes.activeStepIcon]: isActive
                    })}
                  >
                    {step.icon}
                  </Avatar>
                }
              >
                <CustomizedTypography variant={!matches ? 'body2' : 'caption'}>
                  {step.label}
                </CustomizedTypography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
};

const useStyles = makeStyles<Theme, ProgressStepStyleProps>((theme) => ({
  container: {
    width: '100%',
    minHeight: '100px',
    height: '100px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderBottom: `1px solid ${colorSet.gray200}`,
    backgroundColor: colorSet.gray100,
    '& .MuiPaper-root': {
      backgroundColor: 'inherit',
      width: 416,
      '& .MuiStep-alternativeLabel': {
        padding: '0px 20px'
      }
    },
    [theme.breakpoints.down('md')]: {
      width: '100%'
    }
  },
  root: {
    height: 84,
    padding: '14px 12px',
    '& .MuiStepConnector-alternativeLabel': {
      top: 13,
      left: 'calc(-50% + 10px)',
      right: 'calc(50% + 10px)',
      width: 'calc(100% - 48px)',
      margin: 'auto',
      zIndex: 1,
      '& .MuiStepConnector-lineHorizontal': {
        borderTopStyle: 'dashed',
        borderTopWidth: 2
      }
    }
  },
  step: {
    padding: 0,
    '& .MuiStepLabel-label': {
      marginTop: 6,
      '& > p': {
        color: colorSet.gray700
      }
    }
  },
  activeStep: {
    '& .MuiStepLabel-label': {
      '& > p': {
        color: ({ isStopped }) =>
          !isStopped ? colorSet.primary500 : colorSet.errorMain
      }
    }
  },
  stepIcon: {
    width: 32,
    height: 32,
    backgroundColor: colorSet.gray600,
    border: `1px solid ${colorSet.gray400}`,
    '& svg': {
      width: 16,
      height: 16,
      color: colorSet.gray000
    },
    [theme.breakpoints.down('md')]: {
      width: 28,
      height: 28
    }
  },
  activeStepIcon: {
    backgroundColor: ({ isStopped }) =>
      !isStopped ? colorSet.primary500 : colorSet.errorMain,
    border: 'none',
    '& svg': {
      color: colorSet.gray000
    }
  }
}));

export default ProgressStep;
